import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Select from "react-select";
import api from "../../api";
import svblogo from "../../Logo/svblogo.png";
import authorizedSignature from "../../Logo/MPS.png";
import "./QuotationPage.css";

const COMPANY_INFO = {
  SVB: {
    name: "Softvolute Buildcon LLP",
    address:
      "302, Union Trade Center, Near Apple Hospital, Udhna Darwaja, Surat - 395007",
    email: "financesvbllp@gmail.com",
    phone: "9898608595",
    gstin: "",
  },
};

const UNIT_OPTIONS = [
  "Sq. Ft.",
  "Sq. M.",
  "Running Ft.",
  "Running M.",
  "Acre",
  "Yard",
  "Nos.",
  "Hours",
  "Days",
  "Lump Sum",
  "Percentage",
];

const emptyItem = (section = "Architecture") => ({
  section,
  service: "",
  serviceName: "",
  description: "",
  quantity: 1,
  unit: "Sq. Ft.",
  rate: 0,
  discountType: "none",
  discountValue: 0,
  gstRate: 18,
  hsnCode: "",
});

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);

const formatAddress = (address) => {
  if (!address) return "";

  if (typeof address === "string") {
    return address;
  }

  if (typeof address === "object") {
    return [
      address.address,
      address.city,
      address.state,
      address.country,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return "";
};

const cleanSectionName = (value = "") =>
  String(value).trim().replace(/\s+/g, " ");

const getServiceId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
};

const SEARCH_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderColor: state.isFocused ? "#94a3b8" : "#dbe3ee",
    boxShadow: state.isFocused ? "0 0 0 1px #94a3b8" : "none",
    fontSize: 13,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 36,
    padding: "0 10px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 36,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
    fontSize: 13,
  }),
};

const SearchableSelect = ({
  value,
  options,
  onChange,
  placeholder = "Select",
  isClearable = true,
}) => {
  const selectedOption =
    options.find((option) => String(option.value) === String(value ?? "")) ||
    null;

  return (
    <Select
      value={selectedOption}
      options={options}
      onChange={(option) => onChange(option?.value ?? "")}
      placeholder={placeholder}
      isSearchable
      isClearable={isClearable}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      noOptionsMessage={({ inputValue }) =>
        inputValue ? `No result for "${inputValue}"` : "No options"
      }
      styles={SEARCH_SELECT_STYLES}
    />
  );
};

const QuotationPage = () => {
  const previewRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [termsMaster, setTermsMaster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentQuotation, setPaymentQuotation] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: "NEFT",
    amount: "",
    paymentDate: today(),
    senderName: "",
    senderBankName: "",
    senderAccountNumber: "",
    transactionNumber: "",
    chequeNumber: "",
    chequeDate: "",
    chequeBankName: "",
    remarks: "",
    proofFile: null,
  });
  const getPaymentProofUrl = (proofUrl = "") => {
    const value = String(proofUrl || "").trim();

    if (!value) return "";

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:") ||
      value.startsWith("blob:")
    ) {
      return value;
    }

    const apiBase = String(api.defaults?.baseURL || "").replace(/\/+$/, "");

    let serverBase = apiBase;

    if (/\/api$/i.test(serverBase)) {
      serverBase = serverBase.replace(/\/api$/i, "");
    }

    if (!/^https?:\/\//i.test(serverBase)) {
      serverBase = window.location.origin;
    }

    return `${serverBase}${value.startsWith("/") ? "" : "/"}${value}`;
  };
  const getCurrentFinancialYearStart = () => {
    const now = new Date();
    const year = now.getFullYear();
    return now.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearForDate = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    return date.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearLabel = (startYear) =>
    `${startYear}-${String(Number(startYear) + 1).slice(-2)}`;

  const getCalendarYearForFinancialMonth = (financialYearStart, month) =>
    Number(month) >= 4
      ? Number(financialYearStart)
      : Number(financialYearStart) + 1;

  const [quotationFilters, setQuotationFilters] = useState({
    mode: "month",
    financialYear: String(getCurrentFinancialYearStart()),
    month: String(new Date().getMonth() + 1),
    quarter:
      new Date().getMonth() + 1 >= 4
        ? String(Math.floor((new Date().getMonth() + 1 - 4) / 3) + 1)
        : "4",
    fromDate: "",
    toDate: "",
    minTotal: "",
    maxTotal: "",
  });

  const logo = svblogo;

  const [form, setForm] = useState({
    company: "SVB",
    quotationNumber: "",
    client: "",
    projectName: "",
    subject: "",
    documentDate: today(),
    dueDate: "",
    taxMode: "CGST_SGST",
    discountMode: "item",
    overallDiscountType: "none",
    overallDiscountValue: 0,
    bank: "",
    items: [emptyItem()],
    terms: [],
    status: "Draft",
    paidAmount: 0,
  });

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    [],
  );

  const load = async () => {
    try {
      setLoading(true);
      const [d, c, p, s, b, t] = await Promise.all([
        api.get("/sales/documents?type=quotation", { headers }),
        api.get("/sales/clients", { headers }),
        api.get("/projects", { headers }),
        api.get("/sales/services", { headers }),
        api.get("/sales/banks", { headers }),
        api.get("/sales/terms", { headers }),
      ]);

      setDocs(d.data || []);
      setClients(c.data || []);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setServices(s.data || []);
      setBanks(b.data || []);
      setTermsMaster(t.data || []);
    } catch (error) {
      console.error("Failed to load quotation data:", error);
      alert(error.response?.data?.message || "Failed to load quotation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quotationFinancialYears = useMemo(() => {
    const years = new Set();

    docs.forEach((document) => {
      if (!document.documentDate) return;

      const financialYear = getFinancialYearForDate(document.documentDate);
      if (financialYear !== null) {
        years.add(financialYear);
      }
    });

    years.add(getCurrentFinancialYearStart());

    return Array.from(years).sort((a, b) => b - a);
  }, [docs]);

  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const filteredDocs = useMemo(() => {
    return docs.filter((document) => {
      if (!document.documentDate) return false;

      const quotationDate = new Date(document.documentDate);
      if (Number.isNaN(quotationDate.getTime())) return false;

      const mode = quotationFilters.mode || "month";
      const financialYearStart = Number(
        quotationFilters.financialYear || getCurrentFinancialYearStart(),
      );

      if (mode === "month") {
        const selectedMonth = Number(quotationFilters.month || 4);
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          selectedMonth,
        );

        return (
          quotationDate.getFullYear() === calendarYear &&
          quotationDate.getMonth() + 1 === selectedMonth
        );
      }

      if (mode === "quarter") {
        const quarter = Number(quotationFilters.quarter || 1);

        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        return quotationDate >= range.from && quotationDate <= range.to;
      }

      if (mode === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);

        return quotationDate >= from && quotationDate <= to;
      }

      if (mode === "total") {
        const total = Number(document.grandTotal || 0);

        if (
          quotationFilters.minTotal !== "" &&
          total < Number(quotationFilters.minTotal)
        ) {
          return false;
        }

        if (
          quotationFilters.maxTotal !== "" &&
          total > Number(quotationFilters.maxTotal)
        ) {
          return false;
        }

        return true;
      }

      if (mode === "custom") {
        if (quotationFilters.fromDate) {
          const from = new Date(`${quotationFilters.fromDate}T00:00:00`);
          if (quotationDate < from) return false;
        }

        if (quotationFilters.toDate) {
          const to = new Date(`${quotationFilters.toDate}T23:59:59.999`);
          if (quotationDate > to) return false;
        }

        return true;
      }

      return true;
    });
  }, [docs, quotationFilters]);

  const filteredQuotationValue = useMemo(
    () =>
      filteredDocs.reduce(
        (sum, document) => sum + Number(document.grandTotal || 0),
        0,
      ),
    [filteredDocs],
  );

  const clearQuotationFilters = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    setQuotationFilters({
      mode: "month",
      financialYear: String(getCurrentFinancialYearStart()),
      month: String(currentMonth),
      quarter:
        currentMonth >= 4
          ? String(Math.floor((currentMonth - 4) / 3) + 1)
          : "4",
      fromDate: "",
      toDate: "",
      minTotal: "",
      maxTotal: "",
    });
  };

  const selectedClient = useMemo(
    () => clients.find((c) => c._id === form.client),
    [clients, form.client],
  );

  const selectedProject = useMemo(
    () => projects.find((project) => project.name === form.projectName),
    [projects, form.projectName],
  );

  const filteredProjects = useMemo(() => {
    if (!selectedClient) return projects;

    const selectedClientName = String(selectedClient.companyName || "")
      .trim()
      .toLowerCase();

    if (!selectedClientName) return projects;

    const matchedProjects = projects.filter(
      (project) =>
        String(project.clientName || "")
          .trim()
          .toLowerCase() === selectedClientName,
    );

    return matchedProjects.length ? matchedProjects : projects;
  }, [projects, selectedClient]);

  const filteredBanks = useMemo(
    () => banks.filter((b) => !b.company || b.company === "SVB"),
    [banks],
  );

  const selectedBank = useMemo(
    () => banks.find((b) => b._id === form.bank),
    [banks, form.bank],
  );

  const companyInfo = COMPANY_INFO.SVB;

  const sections = useMemo(() => {
    const names = [];

    (form.items || []).forEach((item) => {
      const section = cleanSectionName(item.section || "General");
      if (section && !names.includes(section)) {
        names.push(section);
      }
    });

    return names.length ? names : ["Architecture"];
  }, [form.items]);

  const automaticTermsBySection = useMemo(() => {
    return sections
      .map((section) => {
        const normalizedSection = cleanSectionName(section);

        const matchedRows = termsMaster.filter(
          (row) =>
            String(row.service || "")
              .trim()
              .toLowerCase() === normalizedSection.toLowerCase(),
        );

        const terms = Array.from(
          new Set(
            matchedRows.flatMap((row) =>
              Array.isArray(row.terms) ? row.terms : [],
            ),
          ),
        ).filter(Boolean);

        return {
          section: normalizedSection,
          title: matchedRows[0]?.title || "Terms & Conditions",
          terms,
        };
      })
      .filter((group) => group.terms.length);
  }, [sections, termsMaster]);

  const automaticTerms = useMemo(
    () =>
      Array.from(
        new Set(
          automaticTermsBySection.flatMap((sectionGroup) => sectionGroup.terms),
        ),
      ),
    [automaticTermsBySection],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let itemDiscount = 0;
    let itemTaxBeforeOverall = 0;

    const mode = form.discountMode || "item";

    (form.items || []).forEach((item) => {
      const qty = Number(item.quantity || 0);
      const rate = Number(item.rate || 0);
      const base = qty * rate;

      let discount = 0;

      // Item discount is applied ONLY when Item Wise mode is selected.
      if (mode === "item") {
        if (item.discountType === "percentage") {
          discount = (base * Number(item.discountValue || 0)) / 100;
        } else if (item.discountType === "amount") {
          discount = Number(item.discountValue || 0);
        }
      }

      discount = Math.min(Math.max(discount, 0), base);

      const taxable = base - discount;
      const tax =
        form.taxMode === "NONE"
          ? 0
          : (taxable * Number(item.gstRate || 0)) / 100;

      subtotal += base;
      itemDiscount += discount;
      itemTaxBeforeOverall += tax;
    });

    const afterItemDiscount = subtotal - itemDiscount;

    let overallDiscount = 0;

    // Total quotation discount is applied ONLY when Total Discount mode is selected.
    if (mode === "total") {
      if (form.overallDiscountType === "percentage") {
        overallDiscount =
          (afterItemDiscount * Number(form.overallDiscountValue || 0)) / 100;
      } else if (form.overallDiscountType === "amount") {
        overallDiscount = Number(form.overallDiscountValue || 0);
      }
    }

    overallDiscount = Math.min(Math.max(overallDiscount, 0), afterItemDiscount);

    const taxableAmount = Math.max(0, afterItemDiscount - overallDiscount);

    // Overall discount proportionally reduces the taxable value and therefore GST.
    const ratio = afterItemDiscount > 0 ? taxableAmount / afterItemDiscount : 1;

    const taxTotal = form.taxMode === "NONE" ? 0 : itemTaxBeforeOverall * ratio;

    const cgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const sgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const igst = form.taxMode === "IGST" ? taxTotal : 0;

    return {
      subtotal,
      itemDiscount,
      overallDiscount,
      discountTotal: itemDiscount + overallDiscount,
      taxableAmount,
      taxTotal,
      cgst,
      sgst,
      igst,
      grandTotal: taxableAmount + taxTotal,
    };
  }, [form]);

  const reset = () => {
    setEditing(null);
    setShowPreview(false);
    setForm({
      company: "SVB",
      quotationNumber: "",
      client: "",
      projectName: "",
      subject: "",
      documentDate: today(),
      dueDate: "",
      taxMode: "CGST_SGST",
      discountMode: "item",
      overallDiscountType: "none",
      overallDiscountValue: 0,
      bank: "",
      items: [emptyItem()],
      terms: [],
      status: "Draft",
      paidAmount: 0,
    });
  };

  const updateItem = (index, patch) => {
    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const addSection = () => {
    const name = cleanSectionName(newSectionName);

    if (!name) {
      alert("Enter section name, for example Architecture or Interior");
      return;
    }

    if (
      sections.some((section) => section.toLowerCase() === name.toLowerCase())
    ) {
      alert("This section already exists");
      return;
    }

    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(name)],
    }));
    setNewSectionName("");
  };

  const addServiceToSection = (section) => {
    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(section)],
    }));
  };

  const renameSection = (oldName, newName) => {
    const cleaned = cleanSectionName(newName);
    if (!cleaned) return;

    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item) =>
        cleanSectionName(item.section || "General") === oldName
          ? { ...item, section: cleaned }
          : item,
      ),
    }));
  };

  const removeSection = (section) => {
    const sectionItems = form.items.filter(
      (item) => cleanSectionName(item.section || "General") === section,
    );

    if (
      !window.confirm(
        `Remove "${section}" section and its ${sectionItems.length} service item(s)?`,
      )
    ) {
      return;
    }

    setForm((previous) => {
      const remaining = previous.items.filter(
        (item) => cleanSectionName(item.section || "General") !== section,
      );

      return {
        ...previous,
        items: remaining.length ? remaining : [emptyItem("Architecture")],
      };
    });
  };

  const chooseService = (index, serviceId) => {
    const service = services.find((s) => s._id === serviceId);
    if (!service) {
      updateItem(index, { service: "", serviceName: "" });
      return;
    }

    updateItem(index, {
      service: service._id,
      serviceName: service.name,
      description: service.description || "",
      unit: service.unit || "Sq. Ft.",
      rate: Number(service.defaultRate || 0),
      gstRate: Number(service.gstRate ?? 18),
    });
  };

  const validate = () => {
    if (!form.company) return "Select company";
    if (!String(form.quotationNumber || "").trim())
      return "Enter Quotation No.";
    if (!form.client) return "Select client";
    if (!form.projectName) return "Select project";
    if (!form.documentDate) return "Select quotation date";
    if (!form.items?.length) return "Add at least one service";

    const invalidItem = form.items.some(
      (item) =>
        !cleanSectionName(item.section) ||
        !item.serviceName ||
        Number(item.quantity || 0) <= 0 ||
        Number(item.rate || 0) < 0,
    );

    if (invalidItem) return "Check service, quantity and rate";
    return "";
  };

  const save = async () => {
    const validationMessage = validate();
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        company: "SVB",
        type: "quotation",
        number: String(form.quotationNumber || "").trim(),
        quotationNumber: String(form.quotationNumber || "").trim(),
        client: form.client || null,
        bank: form.bank || null,
        terms: automaticTerms,

        // Only one discount method is sent to backend at a time.
        overallDiscountType:
          form.discountMode === "total" ? form.overallDiscountType : "none",

        overallDiscountValue:
          form.discountMode === "total"
            ? Number(form.overallDiscountValue || 0)
            : 0,

        items: (form.items || []).map((item) => ({
          ...item,
          service: item.service || null,

          discountType:
            form.discountMode === "item" ? item.discountType : "none",

          discountValue:
            form.discountMode === "item" ? Number(item.discountValue || 0) : 0,
        })),
      };

      if (editing) {
        await api.put(`/sales/documents/${editing}`, payload, { headers });
      } else {
        await api.post("/sales/documents", payload, { headers });
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      console.error("Save quotation error:", error);
      alert(error.response?.data?.message || "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const edit = (document) => {
    setEditing(document._id);
    setShowPreview(false);
    setForm({
      ...document,
      company: "SVB",
      quotationNumber: document.quotationNumber || document.number || "",
      client: document.client?._id || document.client || "",
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => ({
            ...item,
            section: cleanSectionName(item.section || "General"),
          }))
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
    });
    setOpen(true);
  };

  const openPaymentModal = (document) => {
    const remaining = Math.max(
      0,
      Number(document.grandTotal || 0) - Number(document.paidAmount || 0),
    );

    setPaymentQuotation(document);
    setPaymentForm({
      paymentMethod: "NEFT",
      amount: remaining ? String(remaining) : String(document.grandTotal || ""),
      paymentDate: today(),
      senderName: "",
      senderBankName: "",
      senderAccountNumber: "",
      transactionNumber: "",
      chequeNumber: "",
      chequeDate: "",
      chequeBankName: "",
      remarks: "",
      proofFile: null,
    });
    setPaymentOpen(true);
  };

  const closePaymentModal = () => {
    if (paymentSaving) return;
    setPaymentOpen(false);
    setPaymentQuotation(null);
  };

  const savePayment = async () => {
    if (!paymentQuotation?._id) return;

    const amount = Number(paymentForm.amount || 0);

    if (amount <= 0) {
      alert("Enter received amount");
      return;
    }

    if (!paymentForm.paymentDate) {
      alert("Select payment date");
      return;
    }

    if (!String(paymentForm.senderName || "").trim()) {
      alert("Enter sender name");
      return;
    }

    if (!String(paymentForm.senderBankName || "").trim()) {
      alert("Enter sender bank name");
      return;
    }

    if (!paymentForm.proofFile) {
      alert("Payment proof screenshot/photo is required");
      return;
    }

    if (
      paymentForm.paymentMethod === "NEFT" &&
      !String(paymentForm.transactionNumber || "").trim()
    ) {
      alert("Enter NEFT / UTR / Transaction No.");
      return;
    }

    if (paymentForm.paymentMethod === "CHEQUE") {
      if (!String(paymentForm.chequeNumber || "").trim()) {
        alert("Enter cheque number");
        return;
      }

      if (!paymentForm.chequeDate) {
        alert("Select cheque date");
        return;
      }

      if (!String(paymentForm.chequeBankName || "").trim()) {
        alert("Enter cheque bank name");
        return;
      }
    }

    try {
      setPaymentSaving(true);

      const fd = new FormData();
      fd.append("paymentMethod", paymentForm.paymentMethod);
      fd.append("amount", String(amount));
      fd.append("paymentDate", paymentForm.paymentDate);
      fd.append("senderName", paymentForm.senderName.trim());
      fd.append("senderBankName", paymentForm.senderBankName.trim());
      fd.append(
        "senderAccountNumber",
        String(paymentForm.senderAccountNumber || "").trim(),
      );
      fd.append(
        "transactionNumber",
        String(paymentForm.transactionNumber || "").trim(),
      );
      fd.append("chequeNumber", String(paymentForm.chequeNumber || "").trim());
      fd.append("chequeDate", paymentForm.chequeDate || "");
      fd.append(
        "chequeBankName",
        String(paymentForm.chequeBankName || "").trim(),
      );
      fd.append("remarks", String(paymentForm.remarks || "").trim());
      fd.append("paymentProof", paymentForm.proofFile);

      await api.post(`/sales/documents/${paymentQuotation._id}/payments`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setPaymentOpen(false);
      setPaymentQuotation(null);
      await load();
      alert("Payment recorded successfully");
    } catch (error) {
      console.error("Record payment error:", error);
      alert(error.response?.data?.message || "Failed to record payment");
    } finally {
      setPaymentSaving(false);
    }
  };

  const remove = async (document) => {
    if (!window.confirm(`Delete ${document.number || "this quotation"}?`)) {
      return;
    }

    try {
      await api.delete(`/sales/documents/${document._id}`, { headers });
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete");
    }
  };

  const convertToInvoice = async (document) => {
    if (!window.confirm("Convert this Quotation to Invoice?")) return;

    try {
      await api.post(
        `/sales/documents/${document._id}/convert`,
        { type: "invoice" },
        { headers },
      );
      alert("Converted to Invoice successfully");
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Conversion failed");
    }
  };

  const openDocumentForPdf = (document) => {
    setEditing(document._id);
    setShowPreview(true);
    setForm({
      ...document,
      company: "SVB",
      quotationNumber: document.quotationNumber || document.number || "",
      client: document.client?._id || document.client || "",
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => ({
            ...item,
            section: cleanSectionName(item.section || "General"),
          }))
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
      number: document.number || "",
    });
    setOpen(true);
  };

  const downloadPdf = async () => {
    if (!previewRef.current) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const imageHeight = (canvas.height * usableWidth) / canvas.width;

      let heightLeft = imageHeight;
      let position = margin;

      pdf.addImage(
        imageData,
        "PNG",
        margin,
        position,
        usableWidth,
        imageHeight,
      );

      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        position = heightLeft - imageHeight + margin;
        pdf.addPage();
        pdf.addImage(
          imageData,
          "PNG",
          margin,
          position,
          usableWidth,
          imageHeight,
        );
        heightLeft -= pageHeight - margin * 2;
      }

      const safeNumber = String(form.number || "QUOTATION").replace(
        /[^\w-]+/g,
        "-",
      );

      pdf.save(`${safeNumber}.pdf`);
    } catch (error) {
      console.error("PDF error:", error);
      alert("PDF could not be generated");
    } finally {
      setDownloading(false);
    }
  };

  const itemAmount = (item) => {
    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;
    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.max(0, base - Math.min(discount, base));
  };

  const itemDiscountAmount = (item) => {
    if ((form.discountMode || "item") !== "item") return 0;

    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;

    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.min(Math.max(discount, 0), base);
  };

  const itemDiscountLabel = (item) => {
    if ((form.discountMode || "item") !== "item") return "-";

    const discountAmount = itemDiscountAmount(item);

    if (!discountAmount) return "-";

    if (item.discountType === "percentage") {
      return `${Number(item.discountValue || 0)}% (₹ ${money(discountAmount)})`;
    }

    if (item.discountType === "amount") {
      return `₹ ${money(discountAmount)}`;
    }

    return "-";
  };

  const itemTaxAmount = (item) => {
    if (form.taxMode === "NONE") return 0;

    const taxable = itemAmount(item);
    return (taxable * Number(item.gstRate || 0)) / 100;
  };

  const itemFinalAmount = (item) => itemAmount(item) + itemTaxAmount(item);

  const totalDiscountLabel = () => {
    if (form.discountMode !== "total" || !totals.overallDiscount) {
      return "";
    }

    if (form.overallDiscountType === "percentage") {
      return `${Number(form.overallDiscountValue || 0)}% (₹ ${money(
        totals.overallDiscount,
      )})`;
    }

    return `₹ ${money(totals.overallDiscount)}`;
  };

  return (
    <div className="pi-page">
      <div className="pi-topbar">
        <div>
          <h1>Quotations</h1>
          <p>Create, manage, preview and download professional quotations.</p>
        </div>

        <button
          className="pi-btn primary"
          onClick={() => {
            reset();
            setShowPreview(false);
            setOpen(true);
          }}
        >
          + Create Quotation
        </button>
      </div>

      <div className="quotation-filter-card">
        <div className="quotation-filter-head">
          <div>
            <h3>Quotation Overview</h3>
            <p>
              View quotations month-wise, quarterly, financial-year-wise, by
              total amount or by custom date range.
            </p>
          </div>

          <button
            type="button"
            className="quotation-filter-clear"
            onClick={clearQuotationFilters}
          >
            Reset
          </button>
        </div>

        <div
          className={`quotation-filter-grid quotation-filter-grid-new quotation-filter-mode-${quotationFilters.mode}`}
        >
          <label>
            View By
            <SearchableSelect
              value={quotationFilters.mode}
              options={[
                { value: "month", label: "Month Wise" },
                { value: "quarter", label: "Quarterly" },
                { value: "year", label: "Financial Year Wise" },
                { value: "total", label: "Total Wise" },
                { value: "custom", label: "Custom Range" },
              ]}
              onChange={(mode) => {
                const now = new Date();

                const currentMonth = now.getMonth() + 1;

                setQuotationFilters((previous) => ({
                  ...previous,
                  mode,
                  financialYear:
                    mode === "custom" || mode === "total"
                      ? ""
                      : String(getCurrentFinancialYearStart()),
                  month: mode === "month" ? String(currentMonth) : "",
                  quarter:
                    mode === "quarter"
                      ? currentMonth >= 4
                        ? String(Math.floor((currentMonth - 4) / 3) + 1)
                        : "4"
                      : "",
                  fromDate: "",
                  toDate: "",
                  minTotal: "",
                  maxTotal: "",
                }));
              }}
              placeholder="Select View"
              isClearable={false}
            />
          </label>

          {quotationFilters.mode !== "custom" &&
            quotationFilters.mode !== "total" && (
              <label>
                Financial Year
                <SearchableSelect
                  value={quotationFilters.financialYear}
                  options={quotationFinancialYears.map((year) => ({
                    value: String(year),
                    label: getFinancialYearLabel(year),
                  }))}
                  onChange={(value) =>
                    setQuotationFilters((previous) => ({
                      ...previous,
                      financialYear: value,
                    }))
                  }
                  placeholder="Select Financial Year"
                  isClearable={false}
                />
              </label>
            )}

          {quotationFilters.mode === "month" && (
            <label>
              Month
              <SearchableSelect
                value={quotationFilters.month}
                options={monthOptions}
                onChange={(value) =>
                  setQuotationFilters((previous) => ({
                    ...previous,
                    month: value,
                  }))
                }
                placeholder="Search Month"
                isClearable={false}
              />
            </label>
          )}

          {quotationFilters.mode === "quarter" && (
            <label>
              Quarter
              <SearchableSelect
                value={quotationFilters.quarter}
                options={[
                  { value: "1", label: "Q1 — Apr to Jun" },
                  { value: "2", label: "Q2 — Jul to Sep" },
                  { value: "3", label: "Q3 — Oct to Dec" },
                  { value: "4", label: "Q4 — Jan to Mar" },
                ]}
                onChange={(value) =>
                  setQuotationFilters((previous) => ({
                    ...previous,
                    quarter: value,
                  }))
                }
                placeholder="Search Quarter"
                isClearable={false}
              />
            </label>
          )}

          {quotationFilters.mode === "total" && (
            <>
              <label>
                Min Total
                <input
                  type="number"
                  min="0"
                  placeholder="Minimum amount"
                  value={quotationFilters.minTotal}
                  onChange={(e) =>
                    setQuotationFilters((previous) => ({
                      ...previous,
                      minTotal: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Max Total
                <input
                  type="number"
                  min="0"
                  placeholder="Maximum amount"
                  value={quotationFilters.maxTotal}
                  onChange={(e) =>
                    setQuotationFilters((previous) => ({
                      ...previous,
                      maxTotal: e.target.value,
                    }))
                  }
                />
              </label>
            </>
          )}

          {quotationFilters.mode === "custom" && (
            <>
              <label>
                From Date
                <input
                  type="date"
                  value={quotationFilters.fromDate}
                  onChange={(e) =>
                    setQuotationFilters((previous) => ({
                      ...previous,
                      fromDate: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                To Date
                <input
                  type="date"
                  min={quotationFilters.fromDate || undefined}
                  value={quotationFilters.toDate}
                  onChange={(e) =>
                    setQuotationFilters((previous) => ({
                      ...previous,
                      toDate: e.target.value,
                    }))
                  }
                />
              </label>
            </>
          )}

          <div className="quotation-filter-summary-inline">
            <div>
              <span>Quotations</span>
              <strong>{filteredDocs.length}</strong>
            </div>

            <div>
              <span>Total</span>
              <strong>₹ {money(filteredQuotationValue)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="pi-card">
        {loading ? (
          <div className="pi-empty">Loading...</div>
        ) : docs.length === 0 ? (
          <div className="pi-empty">No quotations found.</div>
        ) : filteredDocs.length === 0 ? (
          <div className="pi-empty">
            No quotations found for the selected period.
          </div>
        ) : (
          <div className="pi-table-wrap">
            <table className="pi-table">
              <thead>
                <tr>
                  <th>Quotation No.</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((document) => (
                  <tr key={document._id}>
                    <td className="pi-number">{document.number}</td>
                    <td>{document.client?.companyName || "-"}</td>
                    <td>
                      {document.documentDate
                        ? new Date(document.documentDate).toLocaleDateString(
                            "en-IN",
                          )
                        : "-"}
                    </td>
                    <td>{document.projectName || "-"}</td>
                    <td>₹ {money(document.grandTotal)}</td>
                    <td>
                      <span
                        className={`pi-status ${
                          Number(
                            document.balanceAmount ?? document.grandTotal ?? 0,
                          ) <= 0 && Number(document.paidAmount || 0) > 0
                            ? "paid"
                            : Number(document.paidAmount || 0) > 0
                              ? "partial"
                              : ""
                        }`}
                      >
                        {Number(
                          document.balanceAmount ?? document.grandTotal ?? 0,
                        ) <= 0 && Number(document.paidAmount || 0) > 0
                          ? "Paid"
                          : Number(document.paidAmount || 0) > 0
                            ? "Partially Paid"
                            : document.status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <div className="pi-action-row">
                        <button
                          className="pi-link"
                          onClick={() => edit(document)}
                        >
                          Edit
                        </button>
                        <button
                          className="pi-link"
                          onClick={() => openDocumentForPdf(document)}
                        >
                          Preview / PDF
                        </button>
                        <button
                          className={`pi-link ${
                            Number(
                              document.balanceAmount ??
                                document.grandTotal ??
                                0,
                            ) <= 0
                              ? "paid-link"
                              : "payment-link"
                          }`}
                          onClick={() => openPaymentModal(document)}
                        >
                          {Number(
                            document.balanceAmount ?? document.grandTotal ?? 0,
                          ) <= 0
                            ? "Payment Details"
                            : "Mark Paid"}
                        </button>
                        <button
                          className="pi-link danger"
                          onClick={() => remove(document)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div className="pi-modal-overlay">
          <div className="pi-modal">
            <div className="pi-modal-head">
              <div>
                <h2>{editing ? "Edit" : "Create"} Quotation</h2>
                <p>
                  {showPreview
                    ? "Preview the final A4 Quotation."
                    : "Fill the Quotation details."}
                </p>
              </div>
              <div className="pi-modal-head-actions">
                <button
                  type="button"
                  className={`pi-mode-btn ${!showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(false)}
                >
                  Edit Form
                </button>

                <button
                  type="button"
                  className={`pi-mode-btn ${showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </button>

                <button className="pi-close" onClick={() => setOpen(false)}>
                  ✕
                </button>
              </div>
            </div>

            <div className="pi-editor">
              {!showPreview && (
                <div className="pi-form-panel">
                  <section className="pi-section">
                    <h3>Basic Information</h3>

                    <div className="pi-grid four">
                      <label>
                        Company
                        <input
                          type="text"
                          value="SVB"
                          readOnly
                          className="pi-readonly-input"
                        />
                      </label>

                      <label>
                        Quotation No.
                        <input
                          type="text"
                          value={form.quotationNumber || ""}
                          placeholder="e.g. SVB/INV/2026/0001"
                          onChange={(e) =>
                            setForm({
                              ...form,
                              quotationNumber: e.target.value,
                            })
                          }
                        />
                      </label>

                      <label>
                        Client
                        <SearchableSelect
                          value={form.client}
                          options={clients.map((client) => ({
                            value: client._id,
                            label:
                              client.companyName ||
                              client.name ||
                              "Unnamed Client",
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              client: value,
                              projectName: "",
                            })
                          }
                          placeholder="Search / Select Client"
                        />
                      </label>

                      <label>
                        Quotation Date
                        <input
                          type="date"
                          value={form.documentDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, documentDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Due Date
                        <input
                          type="date"
                          value={form.dueDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, dueDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Project
                        <SearchableSelect
                          value={form.projectName || ""}
                          options={filteredProjects.map((project) => ({
                            value: project.name,
                            label: `${project.name || "Unnamed Project"}${
                              project.category ? ` — ${project.category}` : ""
                            }`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              projectName: value,
                            })
                          }
                          placeholder="Search / Select Project"
                        />
                      </label>

                      <label>
                        Subject
                        <input
                          value={form.subject || ""}
                          placeholder="Subject / Scope"
                          onChange={(e) =>
                            setForm({ ...form, subject: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Bank
                        <SearchableSelect
                          value={form.bank || ""}
                          options={filteredBanks.map((bank) => ({
                            value: bank._id,
                            label: `${bank.bankName || "Bank"} - ${bank.accountNumber || ""}`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              bank: value,
                            })
                          }
                          placeholder="Search / Select Bank"
                        />
                      </label>

                      <label>
                        Tax Type
                        <SearchableSelect
                          value={form.taxMode}
                          options={[
                            { value: "CGST_SGST", label: "CGST + SGST" },
                            { value: "IGST", label: "IGST" },
                            { value: "NONE", label: "No GST" },
                          ]}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              taxMode: value || "CGST_SGST",
                            })
                          }
                          placeholder="Search Tax Type"
                          isClearable={false}
                        />
                      </label>
                    </div>
                  </section>

                  <section className="pi-section">
                    <div className="pi-section-title-row">
                      <div>
                        <h3>Service Sections</h3>
                        <p className="pi-section-help">
                          Add Architecture, Interior, Structure, BIM or any
                          other service section in the same Quotation.
                        </p>
                      </div>
                    </div>

                    <div className="pi-add-section-bar">
                      <input
                        value={newSectionName}
                        placeholder="New section name e.g. Interior"
                        onChange={(e) => setNewSectionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSection();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={addSection}
                      >
                        + Add Section
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          const exists = sections.some(
                            (section) =>
                              section.toLowerCase() === "architecture",
                          );
                          if (exists) {
                            addServiceToSection("Architecture");
                          } else {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Architecture"),
                              ],
                            }));
                          }
                        }}
                      >
                        Architecture
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          const exists = sections.some(
                            (section) => section.toLowerCase() === "interior",
                          );
                          if (exists) {
                            addServiceToSection("Interior");
                          } else {
                            setForm((previous) => ({
                              ...previous,
                              items: [...previous.items, emptyItem("Interior")],
                            }));
                          }
                        }}
                      >
                        Interior
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          const exists = sections.some(
                            (section) => section.toLowerCase() === "structure",
                          );
                          if (exists) {
                            addServiceToSection("Structure");
                          } else {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Structure"),
                              ],
                            }));
                          }
                        }}
                      >
                        Structure
                      </button>
                    </div>

                    <div className="pi-service-sections">
                      {sections.map((section) => {
                        const sectionRows = form.items
                          .map((item, index) => ({ item, index }))
                          .filter(
                            ({ item }) =>
                              cleanSectionName(item.section || "General") ===
                              section,
                          );

                        return (
                          <div className="pi-service-section" key={section}>
                            <div className="pi-service-section-head">
                              <div className="pi-section-name-wrap">
                                <span className="pi-section-badge">
                                  SECTION
                                </span>
                                <input
                                  className="pi-section-name-input"
                                  value={section}
                                  onChange={(e) =>
                                    renameSection(section, e.target.value)
                                  }
                                />
                              </div>

                              <div className="pi-service-section-actions">
                                <button
                                  type="button"
                                  className="pi-btn"
                                  onClick={() => addServiceToSection(section)}
                                >
                                  + Add Service
                                </button>

                                {sections.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-btn danger-outline"
                                    onClick={() => removeSection(section)}
                                  >
                                    Remove Section
                                  </button>
                                )}
                              </div>
                            </div>

                            {sectionRows.map(({ item, index }) => (
                              <div
                                className="pi-item-card"
                                key={`${section}-${index}`}
                              >
                                <div className="pi-grid item-grid">
                                  <label className="wide">
                                    Service
                                    <SearchableSelect
                                      value={item.service || ""}
                                      options={services.map((service) => ({
                                        value: service._id,
                                        label:
                                          service.name || "Unnamed Service",
                                      }))}
                                      onChange={(value) =>
                                        chooseService(index, value)
                                      }
                                      placeholder="Search / Select Service"
                                    />
                                  </label>

                                  <label>
                                    Qty
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          quantity: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    Unit
                                    <SearchableSelect
                                      value={item.unit || "Sq. Ft."}
                                      options={UNIT_OPTIONS.map((unit) => ({
                                        value: unit,
                                        label: unit,
                                      }))}
                                      onChange={(value) =>
                                        updateItem(index, {
                                          unit: value || "Sq. Ft.",
                                        })
                                      }
                                      placeholder="Search Unit"
                                      isClearable={false}
                                    />
                                  </label>

                                  <label>
                                    Rate
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.rate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          rate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  {form.discountMode === "item" ? (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-input">
                                        <SearchableSelect
                                          value={item.discountType}
                                          options={[
                                            { value: "none", label: "None" },
                                            { value: "percentage", label: "%" },
                                            { value: "amount", label: "₹" },
                                          ]}
                                          onChange={(value) =>
                                            updateItem(index, {
                                              discountType: value || "none",
                                            })
                                          }
                                          placeholder="Search"
                                          isClearable={false}
                                        />

                                        <input
                                          type="number"
                                          min="0"
                                          value={item.discountValue}
                                          onChange={(e) =>
                                            updateItem(index, {
                                              discountValue: Number(
                                                e.target.value,
                                              ),
                                            })
                                          }
                                        />
                                      </div>
                                    </label>
                                  ) : (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-disabled">
                                        Total discount selected
                                      </div>
                                    </label>
                                  )}

                                  <label>
                                    HSN / SAC Code
                                    <input
                                      type="text"
                                      value={item.hsnCode || ""}
                                      placeholder="e.g. 998321"
                                      onChange={(e) =>
                                        updateItem(index, {
                                          hsnCode: e.target.value,
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    GST %
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.gstRate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          gstRate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>
                                </div>

                                <label className="pi-description-label">
                                  Description
                                  <textarea
                                    value={item.description || ""}
                                    onChange={(e) =>
                                      updateItem(index, {
                                        description: e.target.value,
                                      })
                                    }
                                  />
                                </label>

                                {sectionRows.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-remove-item"
                                    onClick={() =>
                                      setForm((previous) => ({
                                        ...previous,
                                        items: previous.items.filter(
                                          (_, itemIndex) => itemIndex !== index,
                                        ),
                                      }))
                                    }
                                  >
                                    Remove Service
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <section className="pi-section">
                    <h3>Discount & Terms</h3>

                    <div className="pi-discount-mode-box">
                      <div className="pi-discount-mode-title">
                        Apply Discount On
                      </div>

                      <div className="pi-discount-mode-options">
                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "item" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="item"
                            checked={form.discountMode === "item"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "item",
                                overallDiscountType: "none",
                                overallDiscountValue: 0,
                              }))
                            }
                          />
                          <div>
                            <strong>Item Wise Discount</strong>
                            <span>
                              Discount can be different for each service item.
                            </span>
                          </div>
                        </label>

                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "total" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="total"
                            checked={form.discountMode === "total"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "total",
                                items: previous.items.map((item) => ({
                                  ...item,
                                  discountType: "none",
                                  discountValue: 0,
                                })),
                              }))
                            }
                          />
                          <div>
                            <strong>Total Quotation Discount</strong>
                            <span>
                              One discount is applied to the total service
                              value.
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {form.discountMode === "total" && (
                      <div className="pi-grid two pi-total-discount-fields">
                        <label>
                          Total Discount Type
                          <SearchableSelect
                            value={form.overallDiscountType}
                            options={[
                              { value: "none", label: "No Discount" },
                              { value: "percentage", label: "Percentage (%)" },
                              { value: "amount", label: "Fixed Amount (₹)" },
                            ]}
                            onChange={(value) =>
                              setForm({
                                ...form,
                                overallDiscountType: value || "none",
                              })
                            }
                            placeholder="Search Discount Type"
                            isClearable={false}
                          />
                        </label>

                        <label>
                          Total Discount Value
                          <input
                            type="number"
                            min="0"
                            value={form.overallDiscountValue}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                overallDiscountValue: Number(e.target.value),
                              })
                            }
                          />
                        </label>
                      </div>
                    )}

                    <div className="pi-auto-terms">
                      <div className="pi-auto-terms-head">
                        <div>
                          <h4> Terms & Conditions</h4>
                        </div>
                      </div>

                      {automaticTerms.length ? (
                        <ol className="pi-continuous-terms-list form-list">
                          {automaticTerms.map((term, index) => (
                            <li key={index}>{term}</li>
                          ))}
                        </ol>
                      ) : (
                        <div className="pi-no-auto-terms">
                          Add Terms & Conditions in Terms Master for
                          Architecture, Interior or Structure.
                        </div>
                      )}
                    </div>
                  </section>

                  <div className="pi-live-total-card">
                    <div className="pi-live-total-head">
                      <div>
                        <span>Live Quotation Amount</span>
                        <small>
                          Amount updates automatically while you create the
                          quotation.
                        </small>
                      </div>
                      <strong>₹ {money(totals.grandTotal)}</strong>
                    </div>

                    <div className="pi-live-total-grid">
                      <div>
                        <span>Subtotal</span>
                        <strong>₹ {money(totals.subtotal)}</strong>
                      </div>

                      <div>
                        <span>Discount</span>
                        <strong>- ₹ {money(totals.discountTotal)}</strong>
                      </div>

                      <div>
                        <span>Taxable Amount</span>
                        <strong>₹ {money(totals.taxableAmount)}</strong>
                      </div>

                      {form.taxMode === "CGST_SGST" && (
                        <>
                          <div>
                            <span>CGST</span>
                            <strong>₹ {money(totals.cgst)}</strong>
                          </div>
                          <div>
                            <span>SGST</span>
                            <strong>₹ {money(totals.sgst)}</strong>
                          </div>
                        </>
                      )}

                      {form.taxMode === "IGST" && (
                        <div>
                          <span>IGST</span>
                          <strong>₹ {money(totals.igst)}</strong>
                        </div>
                      )}

                      <div className="grand-total">
                        <span>Grand Total</span>
                        <strong>₹ {money(totals.grandTotal)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="pi-form-actions">
                    <button
                      className="pi-btn"
                      type="button"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </button>

                    <button
                      className="pi-btn preview"
                      type="button"
                      onClick={() => setShowPreview(true)}
                    >
                      Preview
                    </button>

                    <button
                      className="pi-btn primary"
                      type="button"
                      onClick={save}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Quotation"}
                    </button>
                  </div>
                </div>
              )}

              {showPreview && (
                <div className="pi-preview-panel preview-only">
                  <div className="pi-preview-toolbar">
                    <div>
                      <div className="pi-preview-label">A4 Preview</div>
                      <span>Review before saving or downloading PDF.</span>
                    </div>

                    <div className="pi-preview-toolbar-actions">
                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => setShowPreview(false)}
                      >
                        ← Back to Edit
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={downloadPdf}
                        disabled={downloading}
                      >
                        {downloading ? "Generating PDF..." : "Download PDF"}
                      </button>

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={save}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Quotation"}
                      </button>
                    </div>
                  </div>

                  <div className="pi-preview-stage">
                    <div className="pi-a4" ref={previewRef}>
                      <header className="pi-doc-header">
                        <div className="pi-brand">
                          {logo ? (
                            <img
                              src={logo}
                              alt="Company logo"
                              className="pi-logo"
                            />
                          ) : (
                            <div className="pi-logo-placeholder">LOGO</div>
                          )}

                          <div>
                            <h2>{companyInfo.name}</h2>
                            <p>{companyInfo.address}</p>
                            {companyInfo.email && <p>{companyInfo.email}</p>}
                            {companyInfo.phone && <p>{companyInfo.phone}</p>}
                            {companyInfo.gstin && (
                              <p>GSTIN: {companyInfo.gstin}</p>
                            )}
                          </div>
                        </div>

                        <div className="pi-doc-title">
                          <h1>QUOTATION</h1>
                          <p>
                            <strong>Quotation No:</strong>{" "}
                            {form.quotationNumber || form.number || "-"}
                          </p>
                          <p>
                            <strong>Date:</strong> {form.documentDate || "-"}
                          </p>
                          <p>
                            <strong>Due Date:</strong> {form.dueDate || "-"}
                          </p>
                        </div>
                      </header>

                      <div className="pi-blue-line" />

                      <section className="pi-party-grid">
                        <div>
                          <span className="pi-small-title">BILL TO</span>
                          <h3>
                            {selectedClient?.companyName || "Select Client"}
                          </h3>
                          <p>{selectedClient?.contactPerson || ""}</p>
                          <p>{formatAddress(selectedClient?.billingAddress)}</p>
                          <p>{selectedClient?.email || ""}</p>
                          <p>{selectedClient?.phone || ""}</p>
                          {selectedClient?.gstin && (
                            <p>GSTIN: {selectedClient.gstin}</p>
                          )}
                        </div>

                        <div className="pi-project-box">
                          <p>
                            <strong>Project:</strong> {form.projectName || "-"}
                          </p>

                          <p>
                            <strong>Subject:</strong> {form.subject || "-"}
                          </p>
                        </div>
                      </section>

                      <div className="inv-doc-items">
                        <div className="inv-doc-items-head">
                          <div>#</div>
                          <div>Service / Description</div>
                          <div>HSN/SAC</div>
                          <div>Qty</div>
                          <div>Unit</div>
                          <div>Unit Rate</div>
                          <div>Discount</div>
                          <div>GST</div>
                          <div>Amount</div>
                        </div>

                        {sections.map((section) => {
                          const sectionRows = form.items
                            .map((item, index) => ({ item, index }))
                            .filter(
                              ({ item }) =>
                                cleanSectionName(item.section || "General") ===
                                section,
                            );

                          const sectionSubtotal = sectionRows.reduce(
                            (sum, { item }) => sum + itemAmount(item),
                            0,
                          );

                          return (
                            <div
                              className="inv-doc-service-section"
                              key={section}
                            >
                              <div className="inv-doc-section-title">
                                {section}
                              </div>

                              {sectionRows.map(({ item, index }) => (
                                <div
                                  className="inv-doc-item-row"
                                  key={`${section}-${index}`}
                                >
                                  <div>{index + 1}</div>

                                  <div className="inv-doc-service-cell">
                                    <strong>
                                      {item.serviceName || "Service"}
                                    </strong>

                                    {item.description && (
                                      <p>{item.description}</p>
                                    )}
                                  </div>

                                  <div>{item.hsnCode || "-"}</div>

                                  <div>{item.quantity}</div>

                                  <div>{item.unit}</div>

                                  <div className="inv-doc-number-cell">
                                    ₹ {money(item.rate)}
                                  </div>

                                  <div className="inv-doc-discount-cell">
                                    {itemDiscountLabel(item)}
                                  </div>

                                  <div className="inv-doc-gst-cell">
                                    {Number(item.gstRate || 0)}%
                                  </div>

                                  <div className="inv-doc-number-cell">
                                    ₹ {money(itemFinalAmount(item))}
                                  </div>
                                </div>
                              ))}

                              <div className="inv-doc-section-total">
                                <span>Section Subtotal</span>
                                <strong>₹ {money(sectionSubtotal)}</strong>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <section className="pi-doc-bottom">
                        <div className="pi-terms-box">
                          <h4>Terms & Conditions</h4>

                          {automaticTerms.length ? (
                            <ol className="pi-continuous-terms-list">
                              {automaticTerms.map((term, index) => (
                                <li key={index}>{term}</li>
                              ))}
                            </ol>
                          ) : (
                            <p>
                              Terms will appear automatically from Terms Master.
                            </p>
                          )}
                        </div>

                        <div className="pi-total-box">
                          <div>
                            <span>Subtotal</span>
                            <b>₹ {money(totals.subtotal)}</b>
                          </div>
                          {form.discountMode === "item" &&
                            totals.itemDiscount > 0 && (
                              <div>
                                <span>Item Discount</span>
                                <b>- ₹ {money(totals.itemDiscount)}</b>
                              </div>
                            )}

                          {form.discountMode === "total" &&
                            totals.overallDiscount > 0 && (
                              <div>
                                <span>Total Discount</span>
                                <b>- {totalDiscountLabel()}</b>
                              </div>
                            )}
                          <div>
                            <span>Taxable Amount</span>
                            <b>₹ {money(totals.taxableAmount)}</b>
                          </div>

                          {form.taxMode === "CGST_SGST" && (
                            <>
                              <div>
                                <span>CGST</span>
                                <b>₹ {money(totals.cgst)}</b>
                              </div>
                              <div>
                                <span>SGST</span>
                                <b>₹ {money(totals.sgst)}</b>
                              </div>
                            </>
                          )}

                          {form.taxMode === "IGST" && (
                            <div>
                              <span>IGST</span>
                              <b>₹ {money(totals.igst)}</b>
                            </div>
                          )}

                          <div className="pi-grand-total">
                            <span>Grand Total</span>
                            <b>₹ {money(totals.grandTotal)}</b>
                          </div>
                          {Number(form.paidAmount || 0) > 0 && (
                            <>
                              <div>
                                <span>Paid Amount</span>
                                <b>₹ {money(form.paidAmount)}</b>
                              </div>
                              <div>
                                <span>Balance</span>
                                <b>
                                  ₹{" "}
                                  {money(
                                    Math.max(
                                      0,
                                      totals.grandTotal -
                                        Number(form.paidAmount || 0),
                                    ),
                                  )}
                                </b>
                              </div>
                            </>
                          )}
                        </div>
                      </section>

                      <section className="pi-bank-box">
                        <h4>Bank Details</h4>
                        {selectedBank ? (
                          <div className="pi-bank-grid">
                            <p>
                              <strong>Bank:</strong>{" "}
                              {selectedBank.bankName || "-"}
                            </p>
                            <p>
                              <strong>A/C Name:</strong>{" "}
                              {selectedBank.accountName || "-"}
                            </p>
                            <p>
                              <strong>A/C No:</strong>{" "}
                              {selectedBank.accountNumber || "-"}
                            </p>
                            <p>
                              <strong>IFSC:</strong> {selectedBank.ifsc || "-"}
                            </p>
                            {selectedBank.swiftCode && (
                              <p>
                                <strong>SWIFT:</strong> {selectedBank.swiftCode}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p>Select a bank account.</p>
                        )}
                      </section>

                      <footer className="pi-doc-footer">
                        <div>
                          <p>
                            This document is a Tax Quotation. Payment status is
                            subject to actual receipt and verification.
                          </p>
                        </div>

                        <div className="pi-signature">
                          <img
                            src={authorizedSignature}
                            alt="Authorized Signatory"
                            className="pi-signature-image"
                          />
                          <div className="pi-sign-line" />
                          <strong>Authorized Signatory</strong>
                          <span>{companyInfo.name}</span>
                        </div>
                      </footer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {paymentOpen && paymentQuotation && (
        <div className="quotation-payment-overlay">
          <div className="quotation-payment-modal">
            <div className="quotation-payment-head">
              <div>
                <h2>
                  {Number(
                    paymentQuotation.balanceAmount ??
                      paymentQuotation.grandTotal ??
                      0,
                  ) <= 0
                    ? "Payment Details"
                    : "Record Payment"}
                </h2>
                <p>
                  Quotation: <strong>{paymentQuotation.number || "-"}</strong>
                </p>
              </div>

              <button
                type="button"
                className="quotation-payment-close"
                onClick={closePaymentModal}
              >
                ✕
              </button>
            </div>

            <div className="quotation-payment-summary">
              <div>
                <span>Quotation Total</span>
                <strong>₹ {money(paymentQuotation.grandTotal)}</strong>
              </div>
              <div>
                <span>Paid</span>
                <strong>₹ {money(paymentQuotation.paidAmount)}</strong>
              </div>
              <div>
                <span>Balance</span>
                <strong>
                  ₹{" "}
                  {money(
                    Math.max(
                      0,
                      Number(paymentQuotation.grandTotal || 0) -
                        Number(paymentQuotation.paidAmount || 0),
                    ),
                  )}
                </strong>
              </div>
            </div>

            {Number(
              paymentQuotation.balanceAmount ??
                paymentQuotation.grandTotal ??
                0,
            ) > 0 ? (
              <>
                <div className="quotation-payment-grid">
                  <label>
                    Payment Method *
                    <SearchableSelect
                      value={paymentForm.paymentMethod}
                      options={[
                        { value: "NEFT", label: "NEFT / UTR" },
                        { value: "CHEQUE", label: "Cheque" },
                      ]}
                      onChange={(value) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          paymentMethod: value || "NEFT",
                        }))
                      }
                      placeholder="Search Payment Method"
                      isClearable={false}
                    />
                  </label>

                  <label>
                    Received Amount *
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          amount: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Payment Date *
                    <input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          paymentDate: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender / Payer Name *
                    <input
                      value={paymentForm.senderName}
                      placeholder="Person / Company name"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderName: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender Bank Name *
                    <input
                      value={paymentForm.senderBankName}
                      placeholder="Bank name"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderBankName: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender Account No.
                    <input
                      value={paymentForm.senderAccountNumber}
                      placeholder="Optional"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderAccountNumber: e.target.value,
                        }))
                      }
                    />
                  </label>

                  {paymentForm.paymentMethod === "NEFT" ? (
                    <label className="quotation-payment-span-2">
                      UTR / Transaction No. *
                      <input
                        value={paymentForm.transactionNumber}
                        placeholder="Enter NEFT / UTR / Transaction reference"
                        onChange={(e) =>
                          setPaymentForm((previous) => ({
                            ...previous,
                            transactionNumber: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ) : (
                    <>
                      <label>
                        Cheque No. *
                        <input
                          value={paymentForm.chequeNumber}
                          placeholder="Cheque number"
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeNumber: e.target.value,
                            }))
                          }
                        />
                      </label>

                      <label>
                        Cheque Date *
                        <input
                          type="date"
                          value={paymentForm.chequeDate}
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeDate: e.target.value,
                            }))
                          }
                        />
                      </label>

                      <label className="quotation-payment-span-2">
                        Cheque Bank Name *
                        <input
                          value={paymentForm.chequeBankName}
                          placeholder="Bank printed on cheque"
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeBankName: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </>
                  )}

                  <label className="quotation-payment-span-2">
                    Payment Proof *
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          proofFile: e.target.files?.[0] || null,
                        }))
                      }
                    />
                    <small>
                      NEFT screenshot / bank receipt or cheque photo is
                      mandatory.
                    </small>
                  </label>

                  <label className="quotation-payment-span-2">
                    Remarks
                    <textarea
                      value={paymentForm.remarks}
                      placeholder="Optional payment note"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          remarks: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="quotation-payment-actions">
                  <button
                    type="button"
                    className="pi-btn"
                    onClick={closePaymentModal}
                    disabled={paymentSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="pi-btn primary"
                    onClick={savePayment}
                    disabled={paymentSaving}
                  >
                    {paymentSaving ? "Saving Payment..." : "Save Payment"}
                  </button>
                </div>
              </>
            ) : (
              <div className="quotation-payment-history">
                <h3>Payment History</h3>

                {Array.isArray(paymentQuotation.payments) &&
                paymentQuotation.payments.length ? (
                  paymentQuotation.payments.map((payment, index) => (
                    <div className="quotation-payment-history-card" key={index}>
                      <div>
                        <span>Method</span>
                        <strong>{payment.paymentMethod || "-"}</strong>
                      </div>
                      <div>
                        <span>Amount</span>
                        <strong>₹ {money(payment.amount)}</strong>
                      </div>
                      <div>
                        <span>Date</span>
                        <strong>
                          {payment.paymentDate
                            ? new Date(payment.paymentDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "-"}
                        </strong>
                      </div>
                      <div>
                        <span>Sender</span>
                        <strong>{payment.senderName || "-"}</strong>
                      </div>
                      <div>
                        <span>Bank</span>
                        <strong>{payment.senderBankName || "-"}</strong>
                      </div>
                      <div>
                        <span>Reference</span>
                        <strong>
                          {payment.paymentMethod === "CHEQUE"
                            ? payment.chequeNumber || "-"
                            : payment.transactionNumber || "-"}
                        </strong>
                      </div>

                      {payment.proofUrl && (
                        <div className="quotation-payment-proof-row">
                          <span>Proof</span>
                          <div className="quotation-payment-proof-row">
                            <span>Payment Proof</span>

                            <a
                              href={getPaymentProofUrl(payment.proofUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={getPaymentProofUrl(payment.proofUrl)}
                                alt="Payment Proof"
                                className="quotation-payment-proof-image"
                              />
                            </a>

                            <a
                              href={getPaymentProofUrl(payment.proofUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View Full Proof
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p>No payment history available.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationPage;
